import { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ShieldAlert, XCircle } from 'lucide-react';
import clsx from 'clsx';
import { ToolHeader } from '../components/ToolHeader';
import { validateEmail } from '../lib/email';

const SAMPLES: ReadonlyArray<{ label: string; value: string }> = [
  { label: '正常', value: 'foo+tag@example.com' },
  { label: '过长 local', value: 'a'.repeat(70) + '@example.com' },
  { label: '连续点', value: 'foo..bar@example.com' },
  { label: 'IDN', value: 'user@bar.中国' },
  { label: '无 @', value: 'foo.example.com' },
];

/**
 * `/tools/validate/email` — RFC 5321/5322 simplified validator.
 *
 * Pure-text input, immediate feedback. The result panel always
 * shows pass/fail at a glance plus the structured issue list, so
 * users learn the rule on the first failure rather than rerunning
 * after each fix.
 *
 * Sample chips above the input give a tour of common failure modes
 * — useful both for new users and for testing the rule set.
 */
export default function EmailTool(): JSX.Element {
  const [input, setInput] = useState<string>(SAMPLES[0].value);
  const result = useMemo(() => validateEmail(input), [input]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <ToolHeader
        toolId="email"
        category="validate"
        description="RFC 5321/5322 简化校验：HTML living-standard 正则 + local 长度 / 连续点号 / IDN 警告 / 整体长度上限。浏览器不做 DNS / SMTP 探活，所有判断纯本地完成。"
      />

      <section className="card-pad bg-amber-500/5 border-amber-500/20 flex items-start gap-2 text-[12px] text-amber-700 dark:text-amber-300">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          只校验语法，不查 DNS / MX / SMTP。能不能真的发达，需服务端验证。
        </div>
      </section>

      <section className="card-pad flex flex-col gap-2">
        <header className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-ink-dim">
            示例
          </span>
        </header>
        <div className="flex flex-wrap gap-1.5">
          {SAMPLES.map((s) => (
            <button
              key={s.label}
              type="button"
              onClick={() => setInput(s.value)}
              className="rounded border border-line px-2 py-0.5 text-[12px] text-ink-muted transition hover:border-accent/40 hover:text-ink"
              title={s.value}
            >
              {s.label}
            </button>
          ))}
        </div>
      </section>

      <section className="card-pad flex flex-col gap-2">
        <header className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-ink-dim">
            邮箱
          </span>
          <ResultBadge ok={result.ok} hasWarnings={result.issues.some((i) => i.severity === 'warn')} />
        </header>
        <input
          type="text"
          inputMode="email"
          autoComplete="off"
          spellCheck={false}
          className="input font-mono text-[13px]"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="foo@example.com"
        />
      </section>

      {result.parts && (
        <section className="card-pad flex flex-col gap-1">
          <header className="text-[11px] uppercase tracking-wide text-ink-dim">
            结构
          </header>
          <ul className="text-[12.5px] text-ink">
            <li>
              <span className="w-20 inline-block text-ink-muted">local：</span>
              <code className="font-mono">{result.parts.local}</code>
              <span className="ml-2 text-ink-dim">{result.parts.local.length} 字符</span>
            </li>
            <li>
              <span className="w-20 inline-block text-ink-muted">domain：</span>
              <code className="font-mono">{result.parts.domain}</code>
              <span className="ml-2 text-ink-dim">{result.parts.domain.length} 字符</span>
            </li>
          </ul>
        </section>
      )}

      <section className="card-pad flex min-h-0 flex-1 flex-col gap-1">
        <header className="text-[11px] uppercase tracking-wide text-ink-dim">
          诊断（{result.issues.length}）
        </header>
        {result.issues.length === 0 ? (
          <p className="text-[12.5px] text-emerald-600 dark:text-emerald-400">
            未检测到任何问题。
          </p>
        ) : (
          <ul className="divide-y divide-line/40">
            {result.issues.map((i, idx) => (
              <li key={idx} className="flex items-start gap-2 px-1 py-1.5">
                {i.severity === 'error' ? (
                  <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />
                ) : (
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] text-ink">{i.message}</div>
                  <div className="text-[10.5px] font-mono text-ink-dim">{i.code}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function ResultBadge({
  ok,
  hasWarnings,
}: {
  ok: boolean;
  hasWarnings: boolean;
}): JSX.Element {
  if (!ok) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-red-500/30 bg-red-500/10 px-2 py-0.5 text-[11px] text-red-600 dark:text-red-400">
        <XCircle className="h-3 w-3" />
        无效
      </span>
    );
  }
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]',
        hasWarnings
          ? 'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400'
          : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
      )}
    >
      <CheckCircle2 className="h-3 w-3" />
      {hasWarnings ? '有效（含警告）' : '有效'}
    </span>
  );
}
